/**
 * Cadastral perimeter survey points.
 *
 * Source: Venezuelan cadastral coordinates, UTM Zone 20N.
 * Format in the source: "norte:este" (north:east) in meters, with
 * `.` as thousands separator and `,` as decimal (e.g. 1.204.308,28).
 *
 * Datum: PSAD56 La Canoa (historical Venezuelan cadastral datum;
 * often colloquially called "REGVEN" even though strict REGVEN is
 * SIRGAS / WGS84-aligned). Converted to WGS84 lat/lon via the
 * PSAD56 UTM inverse plus 3-parameter Helmert shift
 * (EPSG 1247: dx=-270.933, dy=115.599, dz=-360.226).
 *
 * The 13 points trace the property perimeter. A subset (1, 2, 11,
 * 12, 13) forms the simplified pentagon used as the property outline
 * (see PERIMETER_POINT_NUMBERS in refPointsLayer.ts).
 */

export interface RefPoint {
  id: string;
  label: string;
  utm: { east: number; north: number };
  lat: number;
  lon: number;
}

/**
 * How far (metres) to push the drawn perimeter OUTLINE outward from the true
 * survey border, so it prints just outside the polygon and leaves the interior
 * (lots + streets) visually clean. The P-dots stay on the true corners.
 */
export const PERIMETER_OUTSET_M = 5;

/**
 * Build the perimeter ring (lon/lat) through the given P-numbers, optionally
 * pushed `outsetM` metres radially outward from the ring's centroid. Used by
 * both viewers to draw the yellow outline just outside the actual border.
 */
export function perimeterOutline(
  pointNumbers: number[],
  outsetM = 0,
): { lon: number; lat: number }[] {
  const byNumber = new Map<number, RefPoint>();
  for (const p of REF_POINTS) {
    const n = parseInt(p.label.replace(/^P/, ''), 10);
    if (Number.isFinite(n)) byNumber.set(n, p);
  }
  const pts = pointNumbers
    .map((n) => byNumber.get(n))
    .filter((p): p is RefPoint => Boolean(p));
  if (pts.length === 0 || outsetM === 0) {
    return pts.map((p) => ({ lon: p.lon, lat: p.lat }));
  }
  const cLat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
  const cLon = pts.reduce((s, p) => s + p.lon, 0) / pts.length;
  const mPerDegLon = 111320 * Math.cos((cLat * Math.PI) / 180);
  const mPerDegLat = 110574;
  return pts.map((p) => {
    const dxM = (p.lon - cLon) * mPerDegLon;
    const dyM = (p.lat - cLat) * mPerDegLat;
    const d = Math.hypot(dxM, dyM) || 1;
    return {
      lon: p.lon + ((dxM / d) * outsetM) / mPerDegLon,
      lat: p.lat + ((dyM / d) * outsetM) / mPerDegLat,
    };
  });
}

export const REF_POINTS: RefPoint[] = [
  { id: 'ref-1',  label: 'P1',  utm: { east: 391769.93, north: 1204308.28 }, lat: 10.8896210, lon: -63.9918760 },
  { id: 'ref-2',  label: 'P2',  utm: { east: 391633.94, north: 1205450.21 }, lat: 10.8999222, lon: -63.9933201 },
  { id: 'ref-3',  label: 'P3',  utm: { east: 391487.90, north: 1206676.55 }, lat: 10.9110073, lon: -63.9946931 },
  { id: 'ref-4',  label: 'P4',  utm: { east: 391380.29, north: 1207580.17 }, lat: 10.9191753, lon: -63.9957050 },
  { id: 'ref-5',  label: 'P5',  utm: { east: 391307.36, north: 1208838.06 }, lat: 10.9305479, lon: -63.9964102 },
  { id: 'ref-6',  label: 'P6',  utm: { east: 392733.79, north: 1208737.03 }, lat: 10.9296766, lon: -63.9833548 },
  { id: 'ref-7',  label: 'P7',  utm: { east: 392958.11, north: 1207609.13 }, lat: 10.9194839, lon: -63.9812687 },
  { id: 'ref-8',  label: 'P8',  utm: { east: 392516.73, north: 1207521.35 }, lat: 10.9186771, lon: -63.9853047 },
  { id: 'ref-9',  label: 'P9',  utm: { east: 392575.27, north: 1207227.11 }, lat: 10.9160181, lon: -63.9847603 },
  { id: 'ref-10', label: 'P10', utm: { east: 393223.40, north: 1206275.25 }, lat: 10.9074297, lon: -63.9788018 },
  { id: 'ref-11', label: 'P11', utm: { east: 393403.79, north: 1205808.92 }, lat: 10.9032180, lon: -63.9771375 },
  { id: 'ref-12', label: 'P12', utm: { east: 393566.14, north: 1205389.23 }, lat: 10.8994276, lon: -63.9756397 },
  { id: 'ref-13', label: 'P13', utm: { east: 392000.00, north: 1204758.00 }, lat: 10.8936735, lon: -63.9899502 },
];
